import { customElement, property } from 'lit/decorators.js';
import { CSSResultGroup, html } from 'lit';
import componentStyles from './outline-menu.css.lit';
import { OutlineElement } from '../outline-element/outline-element';

@customElement('outline-menu')
export class OutlineMenu extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Force mobile menu (vertical) display.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'mobile',
  })
  isMobile = false;
  private assignedElements: NodeListOf<HTMLElement>;
  private mainContent: HTMLElement | null;
  private dropdownButtons: Array<HTMLButtonElement>;
  private ariaObserver: MutationObserver | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.createButtonRef();
    this.transformChildLinksToButtons();
    this.toggleMenuInteractionEvents(this.isMobile);
    this.addKeydownListeners();
    this.initializeDropdownMenu();
  }

  initializeDropdownMenu() {
    // Reference to the main content area
    this.mainContent = document.querySelector('main');
    // Select all buttons that can expand/collapse dropdowns
    this.dropdownButtons = Array.from(
      document.querySelectorAll('button.menu__link--has-children')
    );

    // Disconnect any existing observer before creating a new one
    if (this.ariaObserver) {
      this.ariaObserver.disconnect();
    }

    // Create a new observer to watch for changes in aria-expanded attributes
    this.ariaObserver = new MutationObserver(() => {
      this.applyBlurEffectToMainContent();
    });

    // Observe each button for changes in the aria-expanded attribute
    this.dropdownButtons.forEach(button => {
      this.ariaObserver?.observe(button, {
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });
    });
  }

  applyBlurEffectToMainContent() {
    // Check if any dropdown button is expanded
    const isAnyDropdownExpanded = Array.from(this.dropdownButtons).some(
      button => button.getAttribute('aria-expanded') === 'true'
    );

    // Apply or remove blur effect based on the dropdown state
    if (this.mainContent) {
      if (isAnyDropdownExpanded) {
        this.mainContent.classList.add('blur-effect');
      } else {
        this.mainContent.classList.remove('blur-effect');
      }
    }
  }

  createButtonRef() {
    this.assignedElements = document.querySelectorAll(
      'header nav .menu__link.menu__link--link.menu__link--level-1.menu__link--has-children, ' +
        'header nav .menu__link.menu__link--nolink.menu__link--level-1'
    );
  }

  addKeydownListeners() {
    const dropDownMenus = document.querySelectorAll(
      'header nav ul[data-region="header"]'
    );
    this.handleKeydown = this.handleKeydown.bind(this);
    dropDownMenus?.forEach(menu => {
      menu.addEventListener('keydown', this.handleKeydown as EventListener);
    });
  }

  handleKeydown(event: KeyboardEvent) {
    const currentElement = event.target as HTMLElement;
    const menu = currentElement.closest('ul.menu--level-2');

    if (!menu) return;
    // Get focusable elements inside the menu, including those in shadow DOM
    const selector = this.isMobile
      ? 'a, button, .mobile-only tufts-teaser-card, .mobile-only tufts-icon-link'
      : 'a, button, .desktop-only tufts-teaser-card, .desktop-only tufts-icon-link';

    const focusableElements = [...Array.from(menu.querySelectorAll(selector))];

    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    if (event.key === 'Tab') {
      if (event.shiftKey && event.target === firstFocusableElement) {
        event.preventDefault();
        this.closeBlurMenu(event as unknown as FocusEvent, 'prev');
      } else if (!event.shiftKey && event.target === lastFocusableElement) {
        event.preventDefault();
        this.closeBlurMenu(event as unknown as FocusEvent, 'next');
      }
    }
  }

  /**
   * Closes the menu when the last link in the dropdown loses focus.
   * @param {FocusEvent} event - The blur event.
   * @param {string} focusDirection - Determines whether to focus on the next or previous element ('next' or 'prev').
   */
  closeBlurMenu(event: FocusEvent, focusDirection: string) {
    let buttonFound = false;
    let currentElement = event.target as HTMLElement;

    while (
      !buttonFound &&
      !currentElement.classList.contains('menu--level-2')
    ) {
      const closestUL = currentElement.parentElement?.closest('ul');
      const uncleElement = closestUL?.previousElementSibling;

      // Some submenus don't have a dropdown button, the list is always expanded
      // If button found - close it, if not - go up one level
      if (uncleElement instanceof HTMLButtonElement) {
        uncleElement.setAttribute('aria-expanded', 'false');
        buttonFound = true;

        // Move focus to the next or previous top-level menu item
        const topLevelMenuItem = closestUL?.closest('li.menu__item--level-1');
        if (topLevelMenuItem) {
          let targetTopLevelMenuItem: HTMLElement | null = null;
          if (focusDirection === 'next') {
            targetTopLevelMenuItem =
              topLevelMenuItem.nextElementSibling as HTMLElement;
            if (!targetTopLevelMenuItem) {
              // If no next sibling, focus on main-menu-wrapper
              targetTopLevelMenuItem = document
                .querySelector('outline-header')
                ?.shadowRoot?.querySelector(
                  '.main-menu-wrapper'
                ) as HTMLElement;
            }
          } else if (focusDirection === 'prev') {
            targetTopLevelMenuItem =
              topLevelMenuItem.previousElementSibling as HTMLElement;
            if (!targetTopLevelMenuItem) {
              // If no previous sibling, focus on header-wrapper
              targetTopLevelMenuItem = document
                .querySelector('outline-header')
                ?.shadowRoot?.querySelector('.header-wrapper') as HTMLElement;
            }
          }

          if (targetTopLevelMenuItem) {
            const targetButton = targetTopLevelMenuItem.querySelector(
              'button, a'
            ) as HTMLButtonElement;
            if (targetButton) targetButton.focus();
          }
        }
      } else if (closestUL) {
        currentElement = closestUL;
      } else {
        break; // Break the loop if closestUL is false
      }
    }
  }

  /**
   * Closes the menu by setting the aria-expanded attribute of the button to false.
   * @param {Event} event - The event that triggered the menu close.
   */
  closeMenu(event: Event) {
    const currentElement = event.target as HTMLElement;
    const button = currentElement.querySelector('button');
    button?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Toggles menu interaction events based on the device type (mobile or desktop).
   * @param {boolean} isMobile - Indicates if the device is mobile.
   */
  toggleMenuInteractionEvents(isMobile: boolean) {
    // Determine the event method based on the device type
    const toggleEvent = isMobile ? 'removeEventListener' : 'addEventListener';

    // Select all menu items that do not have links
    document
      .querySelectorAll(
        'header nav li.menu__item--nolink, ' + 'header nav li.menu__item--link'
      )
      .forEach(item => {
        // Toggle 'mouseenter' event to open the menu
        item[toggleEvent]('mouseenter', OutlineMenu.openMenu);
        // Toggle 'mouseleave' event to close the menu
        item[toggleEvent]('mouseleave', this.closeMenu);
        // Toggle 'touchstart' event to handle touch interactions
        item[toggleEvent]('touchstart', this.handleTouchStart);
      });
  }

  /**
   * Transforms child links with children into buttons.
   */
  transformChildLinksToButtons() {
    if (this.assignedElements) {
      for (const element of this.assignedElements) {
        // Transform the childElement
        const classes = element.className;
        const title = element.textContent || '';
        const newElement = this.buttonTemplate(classes, true, title);
        if (newElement) {
          element.replaceWith(newElement);
        }
      }
    }
  }

  /**
   * Handles touch start events to prevent mouseover events from triggering the submenu.
   * @param {Event} e - The touchstart event.
   */
  private handleTouchStart(e: Event) {
    const target = e.target as HTMLElement;
    // If touch event, prevent mouseover event from triggering the submenu (gets removed in openMenu function)
    target.closest('li')?.classList.add('is-touch-event');
  }

  /**
   * Opens the menu by setting the aria-expanded attribute of the button to true.
   * @param {Event} e - The event that triggered the menu open.
   */
  static openMenu(e: Event) {
    const target = e.target as HTMLElement;
    // If touch event, prevent mouseover event from triggering the submenu
    if (target.classList.contains('is-touch-event')) {
      target.classList.remove('is-touch-event');
      return;
    }

    // Do not close other menus unless the click is on the top level menu
    if (target.classList.contains('menu--level-1')) {
      OutlineMenu.closeAllMenus(e.target as HTMLButtonElement);
    }

    const toggleButton = target.querySelector('button');
    toggleButton?.setAttribute('aria-expanded', 'true');
  }

  /**
   * Closes all menus except the one associated with the provided toggle button.
   * @param {HTMLButtonElement} toggleButton - The button that triggered the menu close.
   */
  static closeAllMenus(toggleButton: HTMLButtonElement) {
    const menus = document.querySelectorAll('header nav');
    if (menus) {
      [...menus].forEach(menu => {
        const buttons = menu.querySelectorAll('button');
        if (buttons) {
          [...buttons].forEach(button => {
            if (button !== toggleButton) {
              button.setAttribute('aria-expanded', 'false');
            }
          });
        }
      });
    }
  }

  /**
   * Toggles the menu open or close state.
   * @param {Event} event - The event that triggered the toggle.
   */
  toggleMenuOpenClose(event: Event) {
    const target = event.target as HTMLElement;
    const button = target.closest('button');
    if (button) {
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!isExpanded));
      if (!isExpanded) {
        OutlineMenu.closeAllMenus(button);
      }
    }
  }

  /**
   * Creates a button element with the provided classes and title.
   * @param {string} classes - The classes to apply to the button.
   * @param {boolean} showTitle - Whether to show the title.
   * @param {string | undefined} title - The title to display on the button.
   * @returns {HTMLElement | null} - The created button element.
   */
  buttonTemplate(
    classes: string,
    showTitle: boolean,
    title: string | undefined
  ) {
    const drop = classes.includes('has-children');
    const buttonHTML = `
      <button class="${classes} with-title dropdown-button prototype-menu-link" type="button" aria-expanded="false" aria-haspopup="true">
        <span>${showTitle ? title : `${title} sub-navigation`}</span>
      ${
        drop
          ? `<span class="dropdown-icon">
          <tufts-icon name="chevron-down" library="system" size="1rem"></tufts-icon>
        </span>`
          : ``
      }
      </button>
    `;
    const template = document.createElement('template');
    template.innerHTML = buttonHTML.trim();
    const buttonElement = template.content.firstChild;

    if (buttonElement && buttonElement instanceof HTMLElement) {
      buttonElement.addEventListener('click', e => this.toggleMenuOpenClose(e));
    }

    return buttonElement;
  }

  /**
   * Called when the element's properties are updated.
   * @param {Map<string | number | symbol, unknown>} changedProperties - The properties that changed.
   */
  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has('isMobile')) {
      this.updateMenuForMobileView();
    }
  }

  /**
   * Updates the menu for mobile view by adding or removing the 'mobile' class.
   */
  updateMenuForMobileView() {
    const menus = document.querySelectorAll(
      'nav.menu--header-utility, nav.menu--main-menu'
    );
    if (menus.length > 0) {
      menus.forEach(menu => {
        this.isMobile
          ? menu.classList.add('mobile')
          : menu.classList.remove('mobile');
      });
    }
  }

  /**
   * Renders the component's template.
   * @returns {TemplateResult} - The rendered template.
   */
  render() {
    return html` <slot></slot> `;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Disconnect the observer when the component is unmounted
    if (this.ariaObserver) {
      this.ariaObserver.disconnect();
      this.ariaObserver = null;
    }
    // Clean up keyboard event listeners
    const dropDownMenus = document.querySelectorAll(
      'header nav ul[data-region="header"]'
    );
    dropDownMenus?.forEach(menu => {
      menu.removeEventListener('keydown', this.handleKeydown as EventListener);
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-menu': OutlineMenu;
  }
}
